import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { z } from 'zod';

import { Loading } from '#components/loading.js';
import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { RetrievalQueryForm } from './retrieval-query-form.js';
import { RetrievalResultsList } from './retrieval-results-list.js';

const searchSchema = z.object({
	file: z.string().optional(),
});

export const Route = createFileRoute('/_authorization-gate/retrieval-explorer')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'devTools'),
	},
	validateSearch: searchSchema,
	head: () => ({ meta: [{ title: 'Retrieval Explorer • Cadstrom' }] }),
	component: RetrievalExplorerPage,
});

export type RetrievalResult = {
	chunkId: string;
	score: number;
	textContent: string;
};

function RetrievalExplorerPage() {
	const { file: fileId } = Route.useSearch();
	const [results, setResults] = useState<RetrievalResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	return (
		<div className='flex h-screen w-screen flex-col bg-gray-a2'>
			<ResizableLayout split='vertical' initialProportions={['1fr', '2fr']}>
				{/* Left column - Query Form */}
				<div className='h-full overflow-y-auto bg-gray-2 p-6'>
					<h1 className='mb-6 text-2xl font-bold'>Retrieval Explorer</h1>
					<Suspense
						fallback={
							<div className='grid h-full w-full place-items-center'>
								<Loading />
							</div>
						}
					>
						<RetrievalQueryForm initialFileId={fileId} onResults={setResults} onLoadingChange={setIsLoading} />
					</Suspense>
				</div>

				{/* Right column - Results */}
				<div className='h-full overflow-y-auto p-6'>
					<h2 className='mb-4 text-xl font-semibold'>Results</h2>
					{isLoading ?
						<div className='grid h-full w-full place-items-center'>
							<Loading />
						</div>
					:	<RetrievalResultsList results={results} />}
				</div>
			</ResizableLayout>
		</div>
	);
}
